import { INBOX_PAGE_SIZES } from '@ops-desk/contracts';
import { z } from 'zod';
import type { InboxFilters } from './api.ts';

export const INBOX_VIEWS = ['all', 'unread', 'open', 'closed'] as const;

/** URL search params of the inbox list. Invalid values fall back to the defaults. */
export const inboxSearchSchema = z.object({
  view: z.enum(INBOX_VIEWS).optional().catch(undefined),
  q: z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim())
    .pipe(z.string().min(1).max(100))
    .optional()
    .catch(undefined),
  page: z.number().int().min(1).max(10_000).optional().catch(undefined),
  size: z
    .number()
    .refine((n): n is InboxPageSize => (INBOX_PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
});
export type InboxSearch = z.infer<typeof inboxSearchSchema>;
export type InboxView = (typeof INBOX_VIEWS)[number];
export type InboxPageSize = (typeof INBOX_PAGE_SIZES)[number];

/**
 * The list's search params as last shown, so "Service inbox" on a conversation returns to the
 * same page and filters. In memory only: a reload starts from the first page.
 */
let lastListSearch: InboxSearch = {};
export const rememberListSearch = (search: InboxSearch) => {
  lastListSearch = search;
};
export const listSearch = (): InboxSearch => lastListSearch;

/** Server-side filters for a view. */
export function viewFilters(view: InboxView, q?: string): InboxFilters {
  return {
    ...(view === 'unread' ? { unread: true } : {}),
    ...(view === 'open' ? { status: 'open' as const } : {}),
    ...(view === 'closed' ? { status: 'closed' as const } : {}),
    ...(q ? { q } : {}),
  };
}
